import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Eye, Sparkles } from "lucide-react";

const HeadsUpDisplay = ({ userId, onRefresh }) => {
  const [updates, setUpdates] = useState([]);
  const [executing, setExecuting] = useState(false);

  const truncateText = (text, maxLength = 240) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + "...";
  };

  const narrativeUpdates = [
    {
      text: "30 Insurance firms identified with digital transformation budget. Outreach drafted.",
      hasButton: true,
      priority: "low",
      icon: "✓"
    },
    {
      text: "3 accounts have new executives. Personalized value messaging ready.",
      hasButton: false,
      priority: "medium",
      icon: "→"
    },
    {
      text: "Close ratios 5% below peers. Acceleration strategies for top opportunities ready.",
      hasButton: true,
      priority: "high",
      icon: "⚠"
    },
    {
      text: "Competitor active at Johnson Financial. Defensive strategy prepared.",
      hasButton: false,
      priority: "critical",
      icon: "⚡"
    },
    {
      text: "15 healthcare SaaS prospects researched. 8 high-fit companies added.",
      hasButton: true,
      priority: "low",
      icon: "✓"
    },
    {
      text: "AT&T meeting tomorrow. Brief with CFO priorities generated.",
      hasButton: false,
      priority: "medium",
      icon: "→"
    },
    {
      text: "2 deals stalled 14+ days. Re-engagement sequences created.",
      hasButton: true,
      priority: "high",
      icon: "⚠"
    },
    {
      text: "Datadog funding round announced. Expansion opportunity identified.",
      hasButton: false,
      priority: "critical",
      icon: "⚡"
    },
    {
      text: "Coca Cola Q4 planning cycle starts. Timing perfect for upsell pitch.",
      hasButton: false,
      priority: "medium",
      icon: "→"
    },
    {
      text: "Competitor lost OpenAI deal. Window to win their business identified.",
      hasButton: false,
      priority: "critical",
      icon: "⚡"
    },
    {
      text: "12 prospects in fintech sector analyzed. 5 match ICP criteria.",
      hasButton: true,
      priority: "low",
      icon: "✓"
    },
    {
      text: "Pipeline velocity 20% below target. Deal acceleration plan ready.",
      hasButton: true,
      priority: "high",
      icon: "⚠"
    }
  ];

  useEffect(() => {
    setUpdates(narrativeUpdates);
    
    // Generate autonomous activity every 30-90 seconds
    const generateActivity = async () => {
      try {
        const response = await axios.post(`${API}/agent/auto-activity/${userId}`);
        
        // Create concise summary from response
        let summaryText = "Agent completed background analysis.";
        const mode = response.data.modeExecuted || "research";
        
        // Create mode-specific summaries
        if (mode === "hunt") {
          summaryText = `Identified new prospects. ${response.data.result?.prospectsGenerated || 10} companies researched.`;
        } else if (mode === "farm") {
          summaryText = `Analyzed ${response.data.result?.accountsAnalyzed || 5} accounts for expansion opportunities.`;
        } else if (mode === "advisor") {
          const value = response.data.result?.totalPipelineValue || 0;
          summaryText = `Pipeline analyzed. $${(value / 1000).toFixed(0)}K in opportunities reviewed.`;
        }
        
        // Truncate to 240 characters max
        if (summaryText.length > 240) {
          summaryText = summaryText.substring(0, 237) + "...";
        }
        
        // Determine priority based on mode
        const priority = mode === "advisor" ? "high" : mode === "hunt" ? "medium" : "low";
        const icon = mode === "advisor" ? "⚠" : mode === "hunt" ? "→" : "✓";
        
        const activityUpdate = {
          text: summaryText,
          hasButton: true,
          priority: priority,
          icon: icon
        };
        
        setUpdates(prev => [activityUpdate, ...prev.slice(0, 11)]);
      } catch (error) {
        console.error("Error generating activity:", error);
      }
    };
    
    // Initial activity
    generateActivity();
    
    // Auto-update every 30-90 seconds
    const interval = setInterval(() => {
      const randomDelay = 30000 + Math.random() * 60000;
      setTimeout(generateActivity, randomDelay);
    }, 60000);

    return () => clearInterval(interval);
  }, [userId]);

  const handleExecuteAll = async () => {
    setExecuting(true);
    try {
      await axios.post(`${API}/agent/simulate-actions/${userId}`);
      toast.success("All actions executed successfully");
      onRefresh();
    } catch (error) {
      console.error("Error executing actions:", error);
      toast.error("Failed to execute actions");
    } finally {
      setExecuting(false);
    }
  };

  const handleReview = (update) => {
    toast.info("Opening detailed review...");
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case "critical":
        return {
          border: "border-l-red-500",
          bg: "bg-red-500/5",
          text: "text-red-400",
          icon: "text-red-400"
        };
      case "high":
        return {
          border: "border-l-orange-500",
          bg: "bg-orange-500/5",
          text: "text-orange-400",
          icon: "text-orange-400"
        };
      case "medium":
        return {
          border: "border-l-[#3dd9c5]",
          bg: "bg-[#3dd9c5]/5",
          text: "text-white",
          icon: "text-[#3dd9c5]"
        };
      case "low":
        return {
          border: "border-l-green-500",
          bg: "bg-green-500/5",
          text: "text-green-400",
          icon: "text-green-400"
        };
      default:
        return {
          border: "border-l-gray-500",
          bg: "bg-gray-500/5",
          text: "text-white",
          icon: "text-gray-400"
        };
    }
  };

  return (
    <Card className="bg-[#0f1419] border-gray-800/50 p-6 h-[600px] flex flex-col" data-testid="heads-up-display">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#3dd9c5]" />
              Autonomous Stream
              <div className="h-2 w-2 bg-[#3dd9c5] rounded-full animate-pulse ml-2"></div>
            </h2>
            <p className="text-sm text-gray-400">Active · Running continuously</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 pr-4 mb-6">
        <div className="space-y-4">
          {updates.map((update, idx) => {
            const styles = getPriorityStyles(update.priority);
            return (
              <div
                key={idx}
                className={`backdrop-blur-sm border-l-4 ${styles.border} ${styles.bg} rounded-r-lg p-4 hover:bg-white/10 transition-all animate-fade-in`}
                data-testid={`hud-update-${idx}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="flex items-start gap-3">
                  <span className={`text-lg ${styles.icon} flex-shrink-0 font-bold`}>
                    {update.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${styles.text} leading-relaxed`}>
                      {truncateText(update.text, 240)}
                    </p>
                  </div>
                  {update.hasButton && (
                    <Button
                      onClick={() => handleReview(update)}
                      className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white text-xs px-3 py-1 h-auto rounded-full flex-shrink-0"
                      data-testid={`review-btn-${idx}`}
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <Button
        onClick={handleExecuteAll}
        disabled={executing}
        className="w-full bg-[#e91e8c] hover:bg-[#d01a7d] text-white font-semibold py-6 rounded-lg"
        data-testid="execute-all-btn"
      >
        {executing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
            Executing...
          </>
        ) : (
          'Execute all'
        )}
      </Button>
    </Card>
  );
};

export default HeadsUpDisplay;
